import type { ButtonHTMLAttributes, ReactNode } from "react";
import { cn } from "./cn";
import { Ikon, type IkonAdi } from "./Ikon";

export type SatirEylemiTuru = "gor" | "duzenle" | "indir" | "sil";

export interface SatirEylemiProps extends Omit<
  ButtonHTMLAttributes<HTMLButtonElement>,
  "children"
> {
  tur: SatirEylemiTuru;
  /**
   * Erişilebilir ad. Düğmede görünür metin YOK (yalnız ikon), bu yüzden ad
   * zorunlu değil ama varsayılanı türden geliyor: "Görüntüle", "Düzenle"...
   * Satırı söyleyen bir ad daha iyi: "11.03.2026 raporunu sil".
   */
  etiket?: string;
}

/*
  Renkler Veli Raporları ekranından ÖLÇÜLDÜ (eski-tasarim, VeliRaporu-Index):
  göz mor→pembe degrade, düzenle sarı, indir koyu yeşil, sil kırmızı. Sarı
  düğmede ikon KOYU — beyaz ikon sarı üstünde 1,58 veriyor, koyu ikon 10,97.
*/
const TUR: Record<SatirEylemiTuru, { ikon: IkonAdi; etiket: string; sinif: string }> = {
  gor: {
    ikon: "gorunum",
    etiket: "Görüntüle",
    sinif: "text-notr-0 bg-(image:--gradyan-unk-gor)",
  },
  duzenle: { ikon: "duzenle", etiket: "Düzenle", sinif: "text-metin-koyu bg-eylem-duzenle" },
  indir: { ikon: "indir", etiket: "İndir", sinif: "text-notr-0 bg-eylem-indir" },
  sil: { ikon: "sil", etiket: "Sil", sinif: "text-notr-0 bg-eylem-sil" },
};

/**
 * SATIR EYLEMİ — tablo satırının sonundaki küçük renkli kare ikon düğmesi
 * (UNİKOÇ kalıbı, sürüm 9a; tarama-2 §2.9 madde 4).
 *
 * GÖRÜNEN kare 38 px (ölçülen), DOKUNMA alanı 44 px: `after` katmanı kareyi her
 * yönden 3 px büyütüyor. Kareyi 44'e çıkarmak eski panelden sapmak, 38'de
 * bırakmak 03-kod-standartlari'nın dokunma kuralını delmek olurdu.
 *
 * Metin `sr-only` (Ikon kuralı: ikon metnin yerine geçmez, ekran okuyucu metni
 * okur); fareyle bakan için aynı ad `title` ipucunda.
 */
export function SatirEylemi({
  tur,
  etiket,
  className,
  type = "button",
  ...rest
}: SatirEylemiProps) {
  const t = TUR[tur];
  const ad = etiket ?? t.etiket;
  return (
    <button
      {...rest}
      type={type}
      title={rest.title ?? ad}
      data-eylem={tur}
      className={cn(
        "relative inline-grid size-[2.375rem] shrink-0 place-items-center rounded-[0.625rem]",
        "after:absolute after:-inset-[3px] after:content-['']",
        "ease-cikis transition-[translate,box-shadow,opacity] duration-(--sure-hizli)",
        "enabled:hover:shadow-yukselen enabled:hover:-translate-y-px enabled:active:translate-y-0",
        "focus-visible:outline-marka-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2",
        "disabled:cursor-not-allowed disabled:opacity-50",
        t.sinif,
        className,
      )}
    >
      <Ikon ad={t.ikon} className="size-4" />
      <span className="sr-only">{ad}</span>
    </button>
  );
}

/** Satır eylemlerini yan yana dizen kap — aradaki boşluk eski paneldeki gibi 8 px. */
export function SatirEylemleri({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return <div className={cn("flex flex-wrap items-center gap-2", className)}>{children}</div>;
}
